import { useState } from "react";
import { motion } from "framer-motion";
import { Star } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border border-white/15 bg-transparent px-4 py-3.5 text-sm text-[#F3F3F5] placeholder:text-[#3a3a3f] outline-none transition-colors duration-300 focus:border-[#E63946]";
const labelCls =
  "mb-2 block font-mono text-[10px] uppercase tracking-[0.3em] text-[#8E8E93]";

export default function ReviewPage() {
  const [name, setName] = useState("");
  const [projectType, setProjectType] = useState("");
  const [text, setText] = useState("");
  const [rating, setRating] = useState(5);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!name || !text) {
      setError("Name and review are required.");
      return;
    }
    setSending(true);
    setError("");
    try {
      await axios.post(`${API}/reviews`, {
        name,
        project_type: projectType,
        text,
        rating,
      });
      setSent(true);
    } catch (err) {
      setError("Something went wrong sending that. Try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="review-page" className="min-h-screen bg-[#050505] px-5 py-10 text-[#F3F3F5] sm:px-10">
      <div className="mx-auto max-w-xl">
        <a
          data-testid="review-page-logo"
          href="/"
          className="font-mono text-xs font-semibold uppercase tracking-[0.3em] text-white"
        >
          JW. EDITS
        </a>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <h1 className="mt-16 font-display text-5xl font-black uppercase leading-[0.9] tracking-tight sm:text-6xl">
            HOW DID THE EDIT <span className="text-[#E63946]">FEEL?</span>
          </h1>
          <p className="mt-6 text-sm leading-relaxed text-[#8E8E93]">
            Drop a few words about your video — what you sent, what you got back, and how it
            hit. It goes up on the site once Jeremiah approves it.
          </p>

          {sent ? (
            <div
              data-testid="review-success-message"
              className="mt-12 border border-white/15 p-10"
            >
              <span className="animate-rec mb-6 inline-block h-2 w-2 rounded-full bg-[#E63946]" />
              <h2 className="font-display text-4xl font-black uppercase tracking-tight">
                RECEIVED.
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-[#8E8E93]">
                Thank you — your review is in the queue and will appear on the site shortly.
              </p>
              <a
                data-testid="review-back-home-link"
                href="/"
                className="mt-8 inline-block border border-white/25 px-6 py-3 font-mono text-[10px] uppercase tracking-[0.25em] text-[#F3F3F5] transition-colors hover:bg-white hover:text-[#050505]"
              >
                BACK TO JW. EDITS
              </a>
            </div>
          ) : (
            <form data-testid="review-form" onSubmit={submit} className="mt-12 space-y-6">
              <div>
                <label className={labelCls}>YOUR NAME *</label>
                <input
                  data-testid="review-name-input"
                  className={inputCls}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="FIRST NAME IS FINE"
                />
              </div>

              <div>
                <label className={labelCls}>WHAT DID I EDIT FOR YOU?</label>
                <select
                  data-testid="review-type-select"
                  className={`${inputCls} appearance-none bg-[#050505]`}
                  value={projectType}
                  onChange={(e) => setProjectType(e.target.value)}
                >
                  <option value="">SELECT ONE</option>
                  <option>SHORT-FORM CONTENT</option>
                  <option>VLOG EDITING</option>
                  <option>EVENT RECAP</option>
                  <option>BUSINESS & LIFESTYLE</option>
                  <option>CUSTOM EDIT</option>
                </select>
              </div>

              <div>
                <label className={labelCls}>RATING</label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      data-testid={`review-rating-${n}`}
                      type="button"
                      onClick={() => setRating(n)}
                      className="p-1 transition-transform duration-200 hover:scale-110"
                    >
                      <Star
                        className={`h-7 w-7 ${n <= rating ? "fill-[#E63946] text-[#E63946]" : "text-[#3a3a3f]"}`}
                        strokeWidth={1.5}
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className={labelCls}>YOUR REVIEW *</label>
                <textarea
                  data-testid="review-text-textarea"
                  rows={5}
                  className={`${inputCls} resize-none`}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="HOW DID THE FINAL VIDEO TURN OUT?"
                />
              </div>

              {error && (
                <p data-testid="review-error-message" className="font-mono text-xs uppercase tracking-[0.2em] text-[#E63946]">
                  {error}
                </p>
              )}

              <button
                data-testid="review-submit-button"
                type="submit"
                disabled={sending}
                className="w-full bg-[#F3F3F5] px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] text-[#050505] transition-colors duration-300 hover:bg-[#E63946] hover:text-white disabled:opacity-50"
              >
                {sending ? "SENDING..." : "SEND REVIEW"}
              </button>
            </form>
          )}
        </motion.div>

        <p className="mt-16 font-mono text-[10px] uppercase tracking-[0.3em] text-[#3a3a3f]">
          JW. EDITS — REAL-LIFE VIDEO EDITING — EST. 2026
        </p>
      </div>
    </div>
  );
}
